import { useRef, useState } from 'react';
import ModalPortal from '../../components/ModalPortal';
import { apiFetch } from '../../utils/api';
import { GRANTABLE_TOOL_KEYS, tools, visibleToolKeys } from '../../tools/catalog';
import { useToolAccess } from '../../tools/ToolAccessContext';

const ACCESS_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'View', label: 'View' },
    { value: 'Edit', label: 'Edit' }
];

export default function InviteMemberModal({ isOpen, onClose, onInvited, projects = [] }) {
    const { isSuperAdmin } = useToolAccess();
    const grantableToolKeys = visibleToolKeys(GRANTABLE_TOOL_KEYS, isSuperAdmin);
    const overlayMouseDown = useRef(false);
    const [email, setEmail] = useState('');
    const [primaryToolKey, setPrimaryToolKey] = useState('Logic');
    const [defaultProjectId, setDefaultProjectId] = useState('');
    const [extras, setExtras] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const resetForm = () => {
        setEmail('');
        setPrimaryToolKey('Logic');
        setDefaultProjectId('');
        setExtras({});
        setError('');
        setSaving(false);
    };

    const handleClose = () => {
        if (saving) return;
        resetForm();
        onClose();
    };

    const handleOverlayMouseDown = (e) => {
        if (e.target === e.currentTarget) overlayMouseDown.current = true;
    };

    const handleOverlayMouseUp = (e) => {
        if (e.target === e.currentTarget && overlayMouseDown.current) {
            handleClose();
        }
        overlayMouseDown.current = false;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        const extra = Object.entries(extras)
            .filter(([, access]) => access)
            .filter(([key]) => key !== primaryToolKey)
            .map(([key, access]) => ({ key, access }));
        try {
            const res = await apiFetch('/api/auth/organization/invite', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    primaryToolKey,
                    tools: extra
                })
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body.message || body[0]?.description || 'Invite failed.');
            let message = 'Invitation sent.';
            if (defaultProjectId) {
                const grantRes = await apiFetch(`/api/projects/${defaultProjectId}/access`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: email.trim(), granted: true })
                });
                if (!grantRes.ok) {
                    const grantBody = await grantRes.json().catch(() => ({}));
                    message = grantBody.message || 'Invitation sent, but the default project could not be granted.';
                }
            }
            resetForm();
            await onInvited?.({ message });
            onClose();
        } catch (err) {
            setError(err.message);
            setSaving(false);
        }
    };

    return (
        <ModalPortal>
            <div
                className="modal-overlay"
                onMouseDown={handleOverlayMouseDown}
                onMouseUp={handleOverlayMouseUp}
            >
                <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: '440px', maxWidth: '560px' }}>
                    <h2 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>Invite member</h2>
                    <p style={{ color: 'var(--text-secondary)', margin: '0 0 1.5rem 0', fontSize: '0.9rem' }}>
                        Choose a primary tool and optional extra View or Edit access.
                    </p>

                    {error && (
                        <div style={{ marginBottom: '1rem', color: 'var(--error)', fontSize: '0.9rem' }}>{error}</div>
                    )}

                    <form onSubmit={handleSubmit} className="importer-form" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>
                                Email
                            </label>
                            <input
                                className="input-field"
                                type="email"
                                required
                                placeholder="name@company.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                autoFocus
                                style={{ margin: 0, width: '100%' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>
                                Primary tool
                            </label>
                            <select
                                className="input-field"
                                value={primaryToolKey}
                                onChange={e => setPrimaryToolKey(e.target.value)}
                                style={{ margin: 0, width: '100%' }}
                            >
                                {grantableToolKeys.map(key => (
                                    <option key={key} value={key}>{tools[key].label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>
                                Default project
                            </label>
                            <select
                                className="input-field"
                                value={defaultProjectId}
                                onChange={e => setDefaultProjectId(e.target.value)}
                                style={{ margin: 0, width: '100%' }}
                            >
                                <option value="">None</option>
                                {projects.map(project => (
                                    <option key={project.id} value={project.id}>{project.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>
                                Extra tools
                            </label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                                {grantableToolKeys.filter(key => key !== primaryToolKey).map(key => (
                                    <label key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.9rem' }}>
                                        <span>{tools[key].label}</span>
                                        <select
                                            className="input-field"
                                            value={extras[key] || ''}
                                            onChange={e => setExtras(prev => ({ ...prev, [key]: e.target.value }))}
                                            style={{ margin: 0, width: '120px' }}
                                        >
                                            {ACCESS_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
                            <button
                                type="submit"
                                className="btn-primary"
                                style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)' }}
                                disabled={saving}
                            >
                                {saving ? 'Sending…' : 'Send invite'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </ModalPortal>
    );
}
